import React, { useEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import { useCookies } from 'react-cookie';

import TopBlock, { TopBlockType } from '@/components/blocks/TopBlock';
import TopBlockLoader from '@/components/loaders/TopBlockLoader';
import { DefaultPaths } from '@/components/Page';
import {
  CUSTOMER_COOKIE,
  TOPBLOCK_SELECTOR_LOCAL_STORAGE,
} from '@/utils/constants';

const INIT_DELAY = 50;

type Props = {
  clientVersions: Array<TopBlockType>;
  prospectVersion: TopBlockType;
  selligentOffers: Array<TopBlockType>;
  defaultPaths?: DefaultPaths;
  pageColor?: string;
};

const TopBlockSelector: React.FC<Props> = ({
  clientVersions,
  prospectVersion,
  selligentOffers, // contentful selligent offers
  defaultPaths,
  pageColor,
}) => {
  const [cookies] = useCookies([CUSTOMER_COOKIE]);
  const [selligentUserData, setSelligentUserData] = useState();
  const [topBlock, setTopBlock] = useState(null);

  const initTimerRef = useRef(null);

  const clearTimers = () => {
    clearTimeout(initTimerRef.current);
    initTimerRef.current = null;
  };

  const getClientVersion = () => {
    try {
      const lastTopBlock = localStorage.getItem(
        TOPBLOCK_SELECTOR_LOCAL_STORAGE,
      );
      if (lastTopBlock) {
        const topBlockIds = clientVersions.map(
          (topBlock) => topBlock.contentful_id,
        );
        const position = topBlockIds.indexOf(lastTopBlock);
        if (position !== -1 && position < clientVersions.length - 1) {
          const topBlock = clientVersions[position + 1];
          localStorage.setItem(
            TOPBLOCK_SELECTOR_LOCAL_STORAGE,
            topBlock.contentful_id,
          );
          return topBlock;
        }
      }
      const topBlock = clientVersions[0];
      localStorage.setItem(
        TOPBLOCK_SELECTOR_LOCAL_STORAGE,
        topBlock.contentful_id,
      );
      return topBlock;
    } catch (error) {
      console.error('Error while retrieving client version : ', error);
      return clientVersions[0];
    }
  };

  const useFallbackTopBlocks = (force = false) => {
    if (typeof selligentUserData === 'undefined' && !force) return;

    if (
      clientVersions &&
      clientVersions.length > 0 &&
      cookies[CUSTOMER_COOKIE] === '1'
    ) {
      const topBlock = getClientVersion();
      setTopBlock(topBlock);
    } else {
      setTopBlock(prospectVersion);
    }
  };

  const getActiveOffers = (selligentOffers, cmsContentOffers) => {
    return selligentOffers.map((selligentOffer) => {
      const activeOfferData = cmsContentOffers.find(
        (cmsContentOffer) =>
          cmsContentOffer.selligentOfferName === selligentOffer.name,
      );

      if (activeOfferData) {
        return activeOfferData;
      }

      return false;
    });
  };

  useEffect(() => {
    // when no selligent offers are available we can go for the fallback options
    if (selligentOffers && selligentOffers.length > 0) {
      // we NEED a timer here else the global vars are not ready to be used
      initTimerRef.current = setTimeout(() => {
        // this is the magic link between the selligent script and updating
        // the TopBlockSelector state with selligent data (selligentUserData)
        if (
          typeof window !== 'undefined' &&
          window.hasOwnProperty('bt_trackingFinishedCallbacks')
        ) {
          window['bt_trackingFinishedCallbacks'].push(setSelligentUserData);
        }
      }, INIT_DELAY);
    } else {
      clearTimers();
      useFallbackTopBlocks(true);
    }

    return () => {
      clearTimers();
    };
  }, []);

  useEffect(() => {
    const { offers } = selligentUserData || {};

    // we only take the first offer where "inOffer" is set to true
    // selligentOffers: display content for an offer managed via CMS
    // offers: selligent user data (managed by MKT team)
    if (
      selligentOffers &&
      selligentOffers.length > 0 &&
      offers &&
      offers.length > 0
    ) {
      const finalOffers = offers.filter((offer) => offer.inOffer);

      if (finalOffers && finalOffers.length > 0) {
        const activeOffers = getActiveOffers(finalOffers, selligentOffers);

        if (activeOffers && activeOffers.length > 0) {
          // clear out selligent offers we couldn't find in our CMS cotent
          const finalActiveOffers = activeOffers.filter((x) => x);

          if (
            finalActiveOffers &&
            finalActiveOffers.length > 0 &&
            finalActiveOffers[0].title &&
            finalActiveOffers[0].title.length > 0
          ) {
            setTopBlock(finalActiveOffers[0]);
          } else {
            useFallbackTopBlocks();
          }
        } else {
          useFallbackTopBlocks();
        }
      }
    } else {
      useFallbackTopBlocks();
    }
  }, [selligentUserData]);

  if (!topBlock) return <TopBlockLoader />;

  return (
    <TopBlock {...topBlock} defaultPaths={defaultPaths} pageColor={pageColor} />
  );
};

export default TopBlockSelector;

export const TopBlockSelectorFragmentQuery = graphql`
  fragment TopBlockSelectorFragment on ContentfulTopBlockSelector {
    __typename
    clientVersions {
      ...TopBlockFragment
    }
    prospectVersion {
      ...TopBlockFragment
    }
    selligentOffers {
      ...TopBlockFragment
    }
  }
`;
